const TOP_LAYER_SELECTOR = '[popover]:popover-open, dialog[open]';

/**
 * Nearest open popover or modal dialog above `node`, or `undefined` when there
 * is none.
 *
 * Those hosts render in the browser's top layer, which paints above the whole
 * document no matter what `z-index` anything else carries. A floating layer
 * portalled to `body` from inside one is therefore not just stacked wrong — it
 * is unreachable, and every click on it lands on the host instead. Portalling
 * into the host puts it back in the same layer as the control that opened it.
 *
 * Returns `undefined`, never `null`, so the result can be handed straight to
 * floating-ui's `FloatingPortal`: it reads `root={null}` as "a root is coming,
 * wait for it" and never creates the portal at all, while `undefined` means
 * "default to the body".
 */
export function resolveTopLayerRoot(node: Element | null) {
  if (!node) return undefined;

  // `:popover-open` is a syntax error where the Popover API is unsupported,
  // which would throw out of `closest`; there is no top layer to find either.
  const selector = CSS.supports('selector(:popover-open)')
    ? TOP_LAYER_SELECTOR
    : 'dialog[open]';
  const host = node.closest(selector);

  return host instanceof HTMLElement ? host : undefined;
}
